import { sleep } from "@/modules/common";
import { MateSprite, MateSpriteMode } from "@/types/game";
import CameraController from "phaser3-rex-plugins/plugins/cameracontroller.js";
import ClickOutside from "phaser3-rex-plugins/plugins/clickoutside.js";
import {
  dialogueIntro,
  endIntro,
  eventIntro,
  houseIntro,
  interactionIntro,
  membershipIntro,
} from "../composable/dialogue/configs/house-intro";
import { FrameMode } from "../enums/uiEnums";
import {
  IMateCharacter,
  getSpriteKey,
  loadAvatar,
  loadImageFromSperateFrames,
  loadIndicator,
  loadSpriteIcon,
  mateCharacterProvider,
} from "../utils/character-utils";
import { EventBus } from "../utils/EventBus";
import { showTextBubble } from "../utils/text-bubble-utils";
import { MAP_CONFIGS, MapType } from "../utils/useMap";
import { BaseHouseScene } from "./BaseHouseScene";
import { useMyHouseMap } from "../utils/useMyHouseMap";

export class HouseScene extends BaseHouseScene {
  // @ts-expect-error Autowrided
  rexGestures: GesturesPlugin;
  rexCameraController?: CameraController;
  charMap: Record<string, IMateCharacter & { clickOutside: ClickOutside }> = {};
  mapSprites: Phaser.GameObjects.Sprite[] = [];

  constructor() {
    super("House");
  }

  /** @override */
  init() {
    super.init();
    // 重启场景时清理实例状态，避免沿用constructor阶段初始化的数据
    this.charMap = {} as typeof this.charMap;
    this.mapSprites = [];
    if (this.rexCameraController) {
      this.rexCameraController.destroy();
      this.rexCameraController = undefined;
    }
  }

  preload() {
    super.preload();
    loadSpriteIcon(this);
    loadIndicator(this);

    for (const item of this.houseSceneStore.validSprites) {
      loadImageFromSperateFrames(this, item);
      loadAvatar(item.mate, this);
    }
  }

  create() {
    super.create();

    this.mapSprites = this.mapFactory.createObjects(
      this.map!,
      this.coodinateOffset
    );
    // 基于 wall + furniture 构建隐藏的 collision 层（若支持）
    if ("createCollisionLayer" in this.mapFactory) {
      const collisionLayer = (
        this.mapFactory as ReturnType<typeof useMyHouseMap>
      ).createCollisionLayer(this.map!, this.mapSprites);
      if (collisionLayer) {
        this.tileLayers.push(collisionLayer);
      }
    }
    this.gameUIStore.frameMode = FrameMode.WITH_PANEL;
    this.gameUIStore.dialogueOver();
    // 创建角色
    this.onMateEnter(this.houseSceneStore.validSprites);
    this.supplyAnimation();
    this.supplyMapSpriteInteraction();

    const pan = this.rexGestures.add.pan(this);
    pan.on("pan", () => {
      if (this.houseSceneStore.followingCharId) {
        this.charMap[
          this.houseSceneStore.followingCharId
        ]?.clickOutside.setEnable(false);
        this.toggleCamera(false);
      }
    });

    this.houseSceneStore.startIntervalThinking();
    this.houseSceneStore.resumeRadio();
    if (window.$router.currentRoute.value.query.showTutorial) {
      window.$router.replace({ query: {} });
      this.showIntroDialogue();
    }
  }

  /** @override */
  destroy() {
    super.destroy();
    this.houseSceneStore.stopIntervalThinking();
    this.houseSceneStore.pauseRadio();
  }

  onMateEnter(mateSprite: MateSprite[], needLoadImage = false) {
    if (needLoadImage) {
      this.load.reset();
      for (const m of mateSprite) {
        loadImageFromSperateFrames(this, m);
        loadAvatar(m.mate, this);
      }
      this.load.once("complete", () => {
        this.onMateEnter(mateSprite, false);
      });

      this.load.start();
    } else {
      mateSprite.forEach((m) => {
        if (this.gridEngine.hasCharacter(getSpriteKey(m))) return;
        const config = MAP_CONFIGS[this.houseSceneStore.currentMapType];
        let [x, y] = config.startPoint;
        // 设置出生点
        if (this.houseSceneStore.currentMapType === MapType.MY_HOUSE) {
          const spawnTile = (
            this.mapFactory as ReturnType<typeof useMyHouseMap>
          ).getRandomSpawnTile(this.map!);
          if (spawnTile && spawnTile.x >= 0) {
            x = spawnTile!.x;
            y = spawnTile!.y;
          }
        } else {
          x = Math.floor(Math.random() * config.startRange) + x;
          y = Math.floor(Math.random() * config.startRange) + y;
        }
        const char = mateCharacterProvider({
          scene: this,
          gridEngine: this.gridEngine,
          mateSprite: m,
          x,
          y,
          options: {
            scale:
              this.houseSceneStore.currentMapType === MapType.MY_HOUSE
                ? 0.75
                : 1,
          },
        });
        char.sprite.setInteractive();
        char.sprite.on("pointerdown", () => {
          // 全屏模式下不显示角色详情弹窗
          if (
            this.gameUIStore.frameMode !== FrameMode.FULL_SCREEN_WITH_CONTROL
          ) {
            this.houseSceneStore.showCharacterDetail = true;
          }
          this.toggleCamera(
            true,
            getSpriteKey(m),
            this.gameUIStore.frameMode === FrameMode.FULL_SCREEN_WITH_CONTROL
          );
        });
        const clickOutside = new ClickOutside(char.sprite);
        clickOutside.setEnable(false);
        clickOutside.on("clickoutside", () => {
          if (
            this.houseSceneStore.followingCharId &&
            this.houseSceneStore.followingCharId !== getSpriteKey(m)
          ) {
            clickOutside.setEnable(false);
            return;
          }
          if (
            !this.houseSceneStore.followingCharId ||
            this.houseSceneStore.followingCharId === getSpriteKey(m)
          ) {
            this.toggleCamera(false, getSpriteKey(m));
            clickOutside.setEnable(false);
          }
        });
        this.charMap[char.mateSprite.id!.toString()] = {
          ...char,
          clickOutside,
        };

        this.enterWondering(getSpriteKey(m));
      });
    }
  }

  onMateLeave(mateSprite: MateSprite[]) {
    mateSprite.forEach((m) => {
      if (!this.gridEngine.hasCharacter(getSpriteKey(m))) return;
      this.gridEngine.getSprite(getSpriteKey(m))?.destroy();
      this.gridEngine.getContainer(getSpriteKey(m))?.destroy();
      this.gridEngine.removeCharacter(getSpriteKey(m));
      delete this.charMap[m.id!.toString()];
    });
  }

  /**
   * @override
   */
  setupMainCamera() {
    super.setupMainCamera();
    const { width: mapWidth, height: mapHeight } = this._getMapSize();
    const camera = this.cameras.main;
    // 计算地图中心点
    const mapCenterX = mapWidth / 2;
    const mapCenterY = mapHeight / 2;

    // 设置缩放级别，并确保ratio是0.5的整数倍

    let ratio = Math.min(
      this.scale.width / mapWidth,
      this.scale.height / mapHeight
    );
    ratio = Math.max(1, Math.round(ratio / 1));

    this.houseSceneStore.initialZoom = ratio;
    const extraScale =
      this.houseSceneStore.currentMapType === MapType.MY_HOUSE ? 2 : 1;
    camera.setZoom(ratio * extraScale);

    camera.centerOn(mapCenterX, mapCenterY);

    if (this.rexCameraController) {
      this.rexCameraController.destroy();
    }
    this.rexCameraController = new CameraController(this, {
      camera: this.cameras.main,
      minZoom: this.houseSceneStore.minZoom,
      maxZoom: this.houseSceneStore.maxZoom,
      panScroll: true,
      panScrollEnable: true,
      pinchZoom: true,
      pinchZoomEnable: true,
      pinchZoomMin: this.houseSceneStore.minZoom,
      pinchZoomMax: this.houseSceneStore.maxZoom,
      pinchZoomFocusEnable: true,
      boundsScroll: false,
      boundsScrollEnable: false,
      mouseWheelZoom: true,
      mouseWheelZoomEnable: true,
      mouseWheelZoomStep: 0.1,
      mouseWheelZoomMin: this.houseSceneStore.minZoom,
      mouseWheelZoomMax: this.houseSceneStore.maxZoom,
      enable: true,
    });
    camera.setRoundPixels(true);
  }

  toggleCamera(
    active: boolean,
    charId?: string,
    fullScreenOffset: boolean = false
  ) {
    const prev = this.houseSceneStore.followingCharId
      ? this.charMap[this.houseSceneStore.followingCharId]
      : undefined;
    if (prev) {
      prev.indicator.setVisible(false);
    }
    // 移动摄像机
    if (active) {
      if (!charId) {
        console.log("active camera follow need to set charId");
        return;
      }
      const currentChar = this.charMap[charId!];
      const targetContainer = currentChar!.container;
      const targetMateSprite = currentChar.mateSprite;
      this.cameras.main.setLerp(0.5, 0.5);
      this.cameras.main.zoomTo(1, 500, "Linear");
      this.cameras.main.pan(
        targetContainer.x +
          (targetMateSprite.mode === MateSpriteMode.Compose ? 36 : 24),
        targetContainer.y +
          (targetMateSprite.mode === MateSpriteMode.Compose ? 64 : 48) +
          (fullScreenOffset ? 100 : 0),
        800,
        "Linear",
        true,
        (_camera, progress) => {
          if (progress > 0.99) {
            this.cameras.main.setLerp(0.1, 0.1);
          }
        }
      );
      this.startCameraFollow(currentChar, fullScreenOffset);

      currentChar.indicator.setVisible(true);
      currentChar.clickOutside.setEnable(true);
      this.houseSceneStore.followingCharId = charId;
    } else {
      this.cameras.main.stopFollow();
      this.cameras.main.y = 0;
      this.houseSceneStore.followingCharId = null;
    }
  }

  // 处理尺寸变化
  handleResize() {
    // 重新设置摄像机
    this.setupMainCamera();
  }

  enterWondering(charId: string) {
    this.gridEngine.moveRandomly(charId, 0);
    this.charMap[charId].bulb.setVisible(false);
  }

  enterThinking(charId: string) {
    this.gridEngine.stopMovement(charId);
    this.charMap[charId].bulb.setVisible(true);
  }

  showTextBubble(charId: string, text: string) {
    if (!this.charMap[charId]) return;
    showTextBubble(this, text, this.charMap[charId], {});
  }

  async showIntroDialogue() {
    this.toggleCamera(
      true,
      this.houseSceneStore.validSprites[0].id!.toString()
    );
    await sleep(300);
    EventBus.emit("close-character-detail-popup");
    await this.gameUIStore.showDialogue(houseIntro);
    this.toggleCamera(false);
    this.resetZoom();
    this.houseSessionStore.activeTab = "members";
    await this.gameUIStore.showDialogue(membershipIntro);

    this.houseSessionStore.activeTab = "thoughts";
    await this.gameUIStore.showDialogue(dialogueIntro);

    this.houseSessionStore.activeTab = "events";
    await this.gameUIStore.showDialogue(eventIntro);

    this.houseSessionStore.activeTab = "interaction";
    await this.gameUIStore.showDialogue(interactionIntro);

    await this.gameUIStore.showDialogue(endIntro);
  }

  supplyMapSpriteInteraction() {
    this.mapSprites.forEach((sprite) => {
      if (this.houseSceneStore.currentMapType === MapType.MY_HOUSE) return;
      // 添加物体描边效果
      this.plugins.get("rexOutlinePipeline")?.add(sprite, {
        thickness: 3,
        outlineColor: 0xff8a50,
      });
      // 物品名称展示
      if (sprite.data?.list?.displayName) {
        const text = this.add.text(0, 0, sprite.data.list.displayName, {
          fontSize: "19px",
          fontFamily: "Cubic",
          color: "#fff",
          fontStyle: "bold",
        });
        this.plugins.get("rexOutlinePipeline")?.add(text, {
          thickness: 3,
          outlineColor: 0xff8a50,
        });
        text.setDepth(1001);
        text.setOrigin(0.5, 0.5);

        text.setY(sprite.height / 2 + 10);
        // FIXME: 针对书架暂时写死
        if (sprite.name === "bookshelf") {
          text.setY(sprite.height / 2 - 20);
        }
        sprite.parentContainer.add(text);
      }
      // 事件绑定
      sprite
        .setInteractive()
        .on("pointerover", () => {
          this.input.setDefaultCursor("pointer");
        })
        .on("pointerout", () => {
          this.input.setDefaultCursor("default");
        })
        .on("pointerup", () => {
          if (this.rexCameraController?.isPanning) return;
          EventBus.emit("item-click", sprite.texture.key);
        });
    });
  }
}
